import React from "react";
import "./styles.css";
import { Button, Divider, Typography } from "@mui/material";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from "react-router-dom";
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { formatDate } from "../../utils/patientUtils";
import { handleSetReferral, DR_GRADE_STYLES, DEFAULT_DR_GRADE_STYLE } from "../../utlis";
 
// const PatientRecordsReport = ({report, inference}) => {
const PatientRecordsReport = ({ report, inference, dme = { left: null, right: null }, isMobile, dataSource, optometristRole, facilities = [] }) => {
    const navigate = useNavigate();
    
    // Handle missing data - support both field naming conventions
    const lastVisitDate = report?.created_at || report?.createdAt ?
        formatDate(report.created_at || report.createdAt) : "NA";
    const visitCount = report?.number_of_visits || "NA";

    // District-wide view (CRD v4.2 US1): each row can belong to a different facility.
    // getreport/ only returns the facility as a bare id (patient.registration_health_facility),
    // not a name, so resolve it against the district's facility list (the same one the
    // Facility dropdown uses).
    const facilityId = report?.patient?.registration_health_facility;
    const facilityName = optometristRole === "Ophthalmologist" && facilityId
        ? (facilities.find((f) => f.id === facilityId)?.name || null)
        : null;
    
    // Check if assessment is completed for ophthalmologist
    const isAssessmentCompleted = optometristRole === "Ophthalmologist" && 
        (report?.feedback_data || report?.feedback_submitted_at);
    const assessmentStatus = isAssessmentCompleted ? "Assessment Completed" : (optometristRole === "Ophthalmologist" ? "Assessment Pending" : null);

    // Helper function to normalize DR grade from model output (moved before handleHeaderDivClassname)
    const normalizeDRGradeForHeader = (drGrade) => {
        if (!drGrade) return null;
        // Map various formats to standard names
        const mapping = {
            "No DR": "No Diabetic Retinopathy",
            "No Diabetic Retinopathy": "No Diabetic Retinopathy",
            "Mild Diabetic Retinopathy": "Mild Diabetic Retinopathy",
            "Mild NPDR": "Mild Diabetic Retinopathy",
            "Moderate Diabetic Retinopathy": "Moderate Diabetic Retinopathy",
            "Moderate NPDR": "Moderate Diabetic Retinopathy",
            "Severe Diabetic Retinopathy": "Severe Diabetic Retinopathy",
            "Severe NPDR": "Severe Diabetic Retinopathy",
            "Proliferative Diabetic Retinopathy": "Proliferative Diabetic Retinopathy",
            "PDR": "Proliferative Diabetic Retinopathy"
        };
        return mapping[drGrade] || drGrade;
    };

    const handleHeaderDivClassname = () => {
        const severityRank = {
          "Proliferative Diabetic Retinopathy": 5,
          "Severe Diabetic Retinopathy": 4,
          "Moderate Diabetic Retinopathy": 3,
          "Mild Diabetic Retinopathy": 2,
          "No Diabetic Retinopathy": 1,
          "Non Gradable Image": 0,
        };
      
        const classMap = {
          5: "eyeInferenceDivProDR",
          4: "eyeInferenceDivSevDR",
          3: "eyeInferenceDivModDR",
          2: "eyeInferenceDivMildDR",
          1: "eyeInferenceDivNoDR",
          0: "eyeInferenceDivHazy",
        };
      
        // Check for operator overrides
        const hasLeftOverride = report?.ml_model?.operator_override?.left?.fundus || false;
        const hasRightOverride = report?.ml_model?.operator_override?.right?.fundus || false;
        
        // Helper to check if eye data exists (has actual DR or DME data)
        const eyeDataExists = (eye) => !!(inference[eye] || dme[eye]);
        
        // Determine effective inference for header color
        let headerInference = null;
        let isOverrideGrey = false; // Flag to track if this is an override grey case
        
        // Rule 1: If override in both eyes → Grey
        if (hasLeftOverride && hasRightOverride) {
            isOverrideGrey = true;
            headerInference = "Non Gradable Image"; // Use this for grey color
        }
        // Rule 2: If override in one eye and other eye is not there (NA) → Grey
        else if (hasLeftOverride && !eyeDataExists("right")) {
            isOverrideGrey = true;
            headerInference = "Non Gradable Image"; // Use this for grey color
        }
        else if (hasRightOverride && !eyeDataExists("left")) {
            isOverrideGrey = true;
            headerInference = "Non Gradable Image"; // Use this for grey color
        }
        // Rule 3: If override in one and other eye has DME (no matter DR) → Red
        // (uses the feedback-corrected DR/DME - via `inference`/`dme` props - the same
        // "final" values used everywhere else on this card, not the raw AI output)
        else if (hasLeftOverride) {
            const rightDMEGrade = dme.right;
            if (rightDMEGrade === "DME") {
                headerInference = "Severe Diabetic Retinopathy"; // Red color
            } else {
                // Rule 4: If override in one and other eye has severe or proliferative → show color of severe/proliferative
                const rightDRGrade = inference.right;
                if (rightDRGrade) {
                    const normalizedDR = normalizeDRGradeForHeader(rightDRGrade);
                    if (normalizedDR === "Severe Diabetic Retinopathy" || normalizedDR === "Proliferative Diabetic Retinopathy") {
                        headerInference = normalizedDR;
                    } else {
                        // Rule 5: If override in one and other eye has mild, no, or moderate → Grey
                        isOverrideGrey = true;
                        headerInference = "Non Gradable Image"; // Use this for grey color
                    }
                } else {
                    // Other eye has no DR data → Grey
                    isOverrideGrey = true;
                    headerInference = "Non Gradable Image"; // Use this for grey color
                }
            }
        }
        else if (hasRightOverride) {
            const leftDMEGrade = dme.left;
            if (leftDMEGrade === "DME") {
                headerInference = "Severe Diabetic Retinopathy"; // Red color
            } else {
                // Rule 4: If override in one and other eye has severe or proliferative → show color of severe/proliferative
                const leftDRGrade = inference.left;
                if (leftDRGrade) {
                    const normalizedDR = normalizeDRGradeForHeader(leftDRGrade);
                    if (normalizedDR === "Severe Diabetic Retinopathy" || normalizedDR === "Proliferative Diabetic Retinopathy") {
                        headerInference = normalizedDR;
                    } else {
                        // Rule 5: If override in one and other eye has mild, no, or moderate → Grey
                        isOverrideGrey = true;
                        headerInference = "Non Gradable Image"; // Use this for grey color
                    }
                } else {
                    // Other eye has no DR data → Grey
                    isOverrideGrey = true;
                    headerInference = "Non Gradable Image"; // Use this for grey color
                }
            }
        }
        // Rule 6: If both have no override then check for DME, if DME show red, if no DME then normally check priority
        else {
            // Check for DME in either eye (priority)
            const leftDMEGrade = dme.left;
            const rightDMEGrade = dme.right;

            if (leftDMEGrade === "DME" || rightDMEGrade === "DME") {
                // If DME exists, show red - use Severe for red color
                headerInference = "Severe Diabetic Retinopathy";
            } else {
                // No DME - use normal inference priority (as it used to happen before)
                const leftRank = inference.left ? (severityRank[inference.left] ?? 0) : 0;
                const rightRank = inference.right ? (severityRank[inference.right] ?? 0) : 0;
                const maxRank = Math.max(leftRank, rightRank);
                headerInference = Object.keys(severityRank).find(key => severityRank[key] === maxRank) || inference.left || inference.right;
            }
        }
        
        const headerRank = headerInference ? (severityRank[headerInference] ?? 0) : 0;
        const className = classMap[headerRank] || "eyeInferenceDivNoDR"; // Default to NoDR if no rank
        return `patientRecordsReportHeader ${className}`;

      };
      

    const handleLeftEyeColor = () => {
        const inf = inference.left;
    
        if (inf === "No Diabetic Retinopathy") {
            return "eyeInferenceTextNoDR";
        } else if (inf === "Mild Diabetic Retinopathy") {
            return "eyeInferenceTextMildDR";
        } else if (inf === "Moderate Diabetic Retinopathy") {
            return "eyeInferenceTextModDR";
        } else if (inf === "Severe Diabetic Retinopathy") {
            return "eyeInferenceTextSevDR";
        } else if (inf === "Proliferative Diabetic Retinopathy") {
            return "eyeInferenceTextProDR";
        } else if (inf === "Non Gradable Image") {
            return "eyeInferenceTextHazy";
        } else if (inf === "NA") {
            return "eyeInferenceTextNA";
        } else {
            return ""; // or a default class
        }
    };

    const handleRightEyeColor = () => {
        const inf = inference.right;
    
        if (inf === "No Diabetic Retinopathy") {
            return "eyeInferenceTextNoDR";
        } else if (inf === "Mild Diabetic Retinopathy") {
            return "eyeInferenceTextMildDR";
        } else if (inf === "Moderate Diabetic Retinopathy") {
            return "eyeInferenceTextModDR";
        } else if (inf === "Severe Diabetic Retinopathy") {
            return "eyeInferenceTextSevDR";
        } else if (inf === "Proliferative Diabetic Retinopathy") {
            return "eyeInferenceTextProDR";
        } else if (inf === "Non Gradable Image") {
            return "eyeInferenceTextHazy";
        } else if (inf === "NA") {
            return "eyeInferenceTextNA";
        } else {
            return ""; // default or fallback class
        }
    };

    const handleViewReport = (id) => {
        // Use report.id for IndexDB data, report.uuid for API data
        const finalId = dataSource === 'indexdb' ? report.id : id;
        navigate(`/patientrecord/${finalId}`);
    };

    function replaceSubstring(inputString) {
        return inputString.replace(/Diabetic Retinopathy/gi, "DR");
    }

    // Helper function to extract DR grade short name
    const getDRGradeShort = (inference) => {
        if (!inference) return null;
        if (inference === "No Diabetic Retinopathy") return "No DR";
        if (inference === "Mild Diabetic Retinopathy") return "Mild";
        if (inference === "Moderate Diabetic Retinopathy") return "Moderate";
        if (inference === "Severe Diabetic Retinopathy") return "Severe";
        if (inference === "Proliferative Diabetic Retinopathy") return "Proliferative";
        if (inference === "Non Gradable Image") return "Non Gradable";
        return null;
    };

    // Helper function to get DME status
    const getDMEStatus = (dmeGrade) => {
        if (!dmeGrade) return null;
        return dmeGrade === "DME" ? "Present" : "Absent";
    };

    // Helper function to get exact DR badge style based on grade
    const getDRBadgeStyle = (inference) => {
        if (!inference) return DEFAULT_DR_GRADE_STYLE;
        return DR_GRADE_STYLES[inference] || DEFAULT_DR_GRADE_STYLE;
    };

    const getDMEColorClass = (dmeGrade) => {
        if (!dmeGrade) return "";
        return dmeGrade === "DME" ? "dmeBadgeRed" : "dmeBadgeGreen";
    };

    // Helper function to normalize DR grade from model output
    const normalizeDRGrade = (drGrade) => {
        if (!drGrade) return null;
        // Map various formats to standard names
        const mapping = {
            "No DR": "No Diabetic Retinopathy",
            "No Diabetic Retinopathy": "No Diabetic Retinopathy",
            "Mild Diabetic Retinopathy": "Mild Diabetic Retinopathy",
            "Mild NPDR": "Mild Diabetic Retinopathy",
            "Moderate Diabetic Retinopathy": "Moderate Diabetic Retinopathy",
            "Moderate NPDR": "Moderate Diabetic Retinopathy",
            "Severe Diabetic Retinopathy": "Severe Diabetic Retinopathy",
            "Severe NPDR": "Severe Diabetic Retinopathy",
            "Proliferative Diabetic Retinopathy": "Proliferative Diabetic Retinopathy",
            "PDR": "Proliferative Diabetic Retinopathy"
        };
        return mapping[drGrade] || drGrade;
    };

    // Helper function to get badge style for override cases
    // This checks the OTHER eye's DR/DME status when one eye has override
    const getOverrideBadgeStyle = (otherEyeDRGrade, otherEyeDMEGrade) => {
        // If other eye has no data (not there) → Grey
        if (!otherEyeDRGrade && !otherEyeDMEGrade) {
            return null; // Grey - return null to use grey text
        }
        
        // Normalize the DR grade
        const normalizedDR = normalizeDRGrade(otherEyeDRGrade);
        
        // Check if DME is present (priority 1: DME overrides everything)
        const hasDME = otherEyeDMEGrade === "DME";
        if (hasDME) {
            return { backgroundColor: "#FFF8F8", color: "#CC0505", border: "1px solid #CC05054D" }; // Red
        }
        
        // Check if DR is severe or proliferative (priority 2)
        const isSevereOrProliferative = normalizedDR === "Severe Diabetic Retinopathy" || 
                                         normalizedDR === "Proliferative Diabetic Retinopathy";
        if (isSevereOrProliferative) {
            if (normalizedDR === "Proliferative Diabetic Retinopathy") {
                return { backgroundColor: "#FFF8F8", color: "#CC0505", border: "1px solid #CC05054D" }; // Red
            } else {
                return { backgroundColor: "#FFFCF5", color: "#D06203", border: "1px solid #D0620347" }; // Orange-red
            }
        }
        
        // Override + (No DR, Mild, Moderate) → Grey
        return null; // Grey - return null to use grey text
    };

  return (
    <div>
        {/* <div className="patientRecordsReportDiv"> */}
        <div className={`patientRecordsReportDiv ${isMobile ? "mobile" : ""}`}>
            <div className={handleHeaderDivClassname()}>
                <div className="patientRecordsReportHeaderDiv">
                    {
                        isMobile ? 
                        (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography style={{ marginRight: '4px' }} fontWeight={600}>{report.patient.other_id}</Typography>
                                    <Divider orientation="vertical" flexItem sx={{ background: "#000000", fontWeight: "600"}}/>
                                    <Typography style={{ marginLeft: '4px' }} fontWeight={600} textTransform="capitalize">{report.patient.age}Y,  {report.patient.sex}</Typography>
                                    {assessmentStatus && (
                                        <>
                                            <Divider orientation="vertical" flexItem sx={{ background: "#000000", fontWeight: "600", marginLeft: '8px', marginRight: '8px'}}/>
                                            <Typography fontWeight={600}>{assessmentStatus}</Typography>
                                        </>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                    {facilityName && (
                                        <>
                                            <Typography fontWeight={400} color="#696969">Facility: {facilityName}</Typography>
                                            <Typography style={{ margin: '0 8px' }} color="#696969">&bull;</Typography>
                                        </>
                                    )}
                                    <Typography fontWeight={400} color="#696969">
                                        {optometristRole === "Ophthalmologist" ? "Last Visit" : "Last Visit Date"}: {lastVisitDate}
                                    </Typography>
                                </div>
                            </>
                            
                         ) :
                        optometristRole === "Ophthalmologist" ? (
                            // District-wide cards carry more info (facility + visit + assessment
                            // status) than fits on one line, so it stacks into two rows here -
                            // the Optometrist branch below is untouched, single-line as before.
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Typography fontWeight={600}>{report.patient.other_id}</Typography>
                                    <Divider orientation="vertical" flexItem sx={{ background: "#000000", fontWeight: "600"}}/>
                                    <Typography fontWeight={600} textTransform="capitalize">{report.patient.age}Y,  {report.patient.sex}</Typography>
                                    {assessmentStatus && (
                                        <>
                                            <Divider orientation="vertical" flexItem sx={{ background: "#000000", fontWeight: "600"}}/>
                                            <Typography fontWeight={600}>{assessmentStatus}</Typography>
                                        </>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    {facilityName && (
                                        <>
                                            <Typography fontWeight={400} color="#696969">Facility: {facilityName}</Typography>
                                            <Typography color="#696969">&bull;</Typography>
                                        </>
                                    )}
                                    <Typography fontWeight={400} color="#696969">Last Visit: {lastVisitDate}</Typography>
                                </div>
                            </div>
                        ) : (
                            <>
                                <Typography fontWeight={600}>{report.patient.other_id}</Typography>
                                <Divider orientation="vertical" flexItem sx={{ background: "#000000", fontWeight: "600"}}/>
                                <Typography fontWeight={600} textTransform="capitalize">{report.patient.age}Y,  {report.patient.sex}</Typography>
                                <Divider orientation="vertical" flexItem sx={{ background: "#000000", fontWeight: "600"}}/>
                                <Typography fontWeight={400} color="#696969">Last Visit Date: {lastVisitDate}</Typography>
                                {assessmentStatus && (
                                    <>
                                        <Divider orientation="vertical" flexItem sx={{ background: "#000000", fontWeight: "600"}}/>
                                        <Typography fontWeight={600}>{assessmentStatus}</Typography>
                                    </>
                                )}
                            </>
                        )
                    }
                </div>
                {!isMobile && <div>
                    <Button
                        disableElevation
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => handleViewReport(report.uuid)}
                        sx={{borderRadius: "3px", border: "1.5px solid var(--white, #FFF)", background: "#FFF", color: "#011F4B"}}
                    >
                        View Report
                    </Button>
                </div>}
            </div>
            <div className="patientRecordsReportInfo">
                <div className="reportDiv">
                    <div className={`patientRecordsReportInfoSub ${isMobile ? "mobileEyeSub" : ""}`}>
                        <Typography color="#696969" fontWeight={500} display="flex" alignItems="center" gap="6px">
                            <VisibilityOutlinedIcon sx={{ fontSize: isMobile ? 18 : undefined }} />
                            Right Eye:
                        </Typography>
                        <div className="drDmeBadgesContainer">
                            {(() => {
                                // Check if there's a fundus override for right eye
                                const hasFundusOverride = report?.ml_model?.operator_override?.right?.fundus || false;
                                
                                // If fundus override exists, show NA with appropriate color coding
                                // Color is based on the OTHER eye's DR/DME status
                                if (hasFundusOverride) {
                                    // Check if both eyes have override → Grey
                                    const hasLeftOverride = report?.ml_model?.operator_override?.left?.fundus || false;
                                    if (hasLeftOverride) {
                                        // Both eyes override → Grey
                                        return (
                                            <>
                                                <div className="drDmeRow">
                                                    <Typography fontWeight={600} style={{ color: "#000" }}>DR</Typography>
                                                    <div className="drValueCell">
                                                        <span className="drBadgeGrey" style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500 }}>
                                                            NA
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="drDmeRow">
                                                    <Typography fontWeight={600} style={{ color: "#000" }}>DME</Typography>
                                                    <span className="dmeBadgeGrey" style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500 }}>
                                                        NA
                                                    </span>
                                                </div>
                                            </>
                                        );
                                    }
                                    
                                    // Get actual DR/DME grade from OTHER eye (left) - feedback-corrected first
                                    const otherEyeDRGrade = inference.left;
                                    const otherEyeDMEGrade = dme.left;
                                    const overrideBadgeStyle = getOverrideBadgeStyle(otherEyeDRGrade, otherEyeDMEGrade);
                                    const isGrey = !overrideBadgeStyle; // Grey if no style

                                    // The single "Is the AI Inference Correct?" toggle is still shown for an
                                    // abstained eye (it covers DR and DME review together - see
                                    // inferenceDetailsComponent.js), so once reviewed, show the doctor's DR
                                    // grade and DME status instead of forcing NA for both.
                                    const rightEyeReviewed = !!report?.feedback_data?.right_eye_inference_is_correct;
                                    const reviewedDRGrade = rightEyeReviewed ? getDRGradeShort(inference.right) : null;
                                    const reviewedDRBadgeStyle = reviewedDRGrade ? getDRBadgeStyle(inference.right) : null;
                                    // DME can't be reliably assessed on a Non Gradable image - suppress whatever
                                    // DME the ophthalmologist recorded when displaying a Non Gradable review.
                                    const reviewedDMEStatus = (rightEyeReviewed && reviewedDRGrade !== "Non Gradable") ? getDMEStatus(dme.right) : null;
                                    const reviewedDMEColorClass = reviewedDMEStatus ? getDMEColorClass(dme.right) : null;

                                    return (
                                        <>
                                            <div className="drDmeRow">
                                                <Typography fontWeight={600} style={{ color: "#000" }}>DR</Typography>
                                                <div className="drValueCell">
                                                    {reviewedDRGrade ? (
                                                        <span style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500, ...reviewedDRBadgeStyle }}>
                                                            {reviewedDRGrade}
                                                        </span>
                                                    ) : isGrey ? (
                                                        <span className="drBadgeGrey" style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500 }}>
                                                            NA
                                                        </span>
                                                    ) : (
                                                        <span style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500, ...overrideBadgeStyle }}>
                                                            NA
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="drDmeRow">
                                                <Typography fontWeight={600} style={{ color: "#000" }}>DME</Typography>
                                                {reviewedDMEStatus ? (
                                                    <span className={reviewedDMEColorClass} style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500 }}>
                                                        {reviewedDMEStatus}
                                                    </span>
                                                ) : isGrey ? (
                                                    <span className="dmeBadgeGrey" style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500 }}>
                                                        NA
                                                    </span>
                                                ) : (
                                                    <span style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500, ...overrideBadgeStyle }}>
                                                        NA
                                                    </span>
                                                )}
                                            </div>
                                        </>
                                    );
                                }

                                const drGrade = getDRGradeShort(inference.right);
                                const dmeGrade = dme.right;
                                // DME can't be reliably assessed on a Non Gradable image - suppress whatever
                                // DME the ophthalmologist recorded when displaying a Non Gradable review.
                                const dmeStatus = drGrade === "Non Gradable" ? null : getDMEStatus(dmeGrade);
                                const hasDR = drGrade !== null && drGrade !== undefined;
                                const hasDME = dmeStatus !== null && dmeStatus !== undefined;
                                
                                // Always show DR and DME rows (use NA when missing) for consistent mobile layout
                                const drBadgeStyle = hasDR ? getDRBadgeStyle(inference.right) : null;
                                
                                return (
                                    <>
                                        <div className="drDmeRow">
                                            <Typography fontWeight={600} style={{ color: "#000" }}>DR</Typography>
                                            <div className="drValueCell">
                                                {hasDR ? (
                                                    <span style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500, ...drBadgeStyle }}>
                                                        {drGrade}
                                                    </span>
                                                ) : (
                                                    <span className="drBadgeGrey" style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500 }}>
                                                        NA
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="drDmeRow">
                                            <Typography fontWeight={600} style={{ color: "#000" }}>DME</Typography>
                                            {hasDME ? (
                                                <span className={getDMEColorClass(dmeGrade)} style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500 }}>
                                                    {dmeStatus}
                                                </span>
                                            ) : (
                                                <span className="dmeBadgeGrey" style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500 }}>
                                                    NA
                                                </span>
                                            )}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                    {!isMobile && <Divider orientation="vertical" variant="middle" flexItem/>}
                    <div className={`patientRecordsReportInfoSub ${isMobile ? "mobileEyeSub" : ""}`} style={!isMobile ? { paddingLeft: "8px" } : undefined}>
                        <Typography color="#696969" fontWeight={500} display="flex" alignItems="center" gap="6px">
                            <VisibilityOutlinedIcon sx={{ fontSize: isMobile ? 18 : undefined }} />
                            Left Eye:
                        </Typography>
                        <div className="drDmeBadgesContainer">
                            {(() => {
                                // Check if there's a fundus override for left eye
                                const hasFundusOverride = report?.ml_model?.operator_override?.left?.fundus || false;
                                
                                // If fundus override exists, show NA with appropriate color coding
                                // Color is based on the OTHER eye's DR/DME status
                                if (hasFundusOverride) {
                                    // Check if both eyes have override → Grey
                                    const hasRightOverride = report?.ml_model?.operator_override?.right?.fundus || false;
                                    if (hasRightOverride) {
                                        // Both eyes override → Grey
                                        return (
                                            <>
                                                <div className="drDmeRow">
                                                    <Typography fontWeight={600} style={{ color: "#000" }}>DR</Typography>
                                                    <div className="drValueCell">
                                                        <span className="drBadgeGrey" style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500 }}>
                                                            NA
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="drDmeRow">
                                                    <Typography fontWeight={600} style={{ color: "#000" }}>DME</Typography>
                                                    <span className="dmeBadgeGrey" style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500 }}>
                                                        NA
                                                    </span>
                                                </div>
                                            </>
                                        );
                                    }
                                    
                                    // Get actual DR/DME grade from OTHER eye (right) - feedback-corrected first
                                    const otherEyeDRGrade = inference.right;
                                    const otherEyeDMEGrade = dme.right;
                                    const overrideBadgeStyle = getOverrideBadgeStyle(otherEyeDRGrade, otherEyeDMEGrade);
                                    const isGrey = !overrideBadgeStyle; // Grey if no style

                                    // The single "Is the AI Inference Correct?" toggle is still shown for an
                                    // abstained eye (it covers DR and DME review together - see
                                    // inferenceDetailsComponent.js), so once reviewed, show the doctor's DR
                                    // grade and DME status instead of forcing NA for both.
                                    const leftEyeReviewed = !!report?.feedback_data?.left_eye_inference_is_correct;
                                    const reviewedDRGrade = leftEyeReviewed ? getDRGradeShort(inference.left) : null;
                                    const reviewedDRBadgeStyle = reviewedDRGrade ? getDRBadgeStyle(inference.left) : null;
                                    // DME can't be reliably assessed on a Non Gradable image - suppress whatever
                                    // DME the ophthalmologist recorded when displaying a Non Gradable review.
                                    const reviewedDMEStatus = (leftEyeReviewed && reviewedDRGrade !== "Non Gradable") ? getDMEStatus(dme.left) : null;
                                    const reviewedDMEColorClass = reviewedDMEStatus ? getDMEColorClass(dme.left) : null;

                                    return (
                                        <>
                                            <div className="drDmeRow">
                                                <Typography fontWeight={600} style={{ color: "#000" }}>DR</Typography>
                                                <div className="drValueCell">
                                                    {reviewedDRGrade ? (
                                                        <span style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500, ...reviewedDRBadgeStyle }}>
                                                            {reviewedDRGrade}
                                                        </span>
                                                    ) : isGrey ? (
                                                        <span className="drBadgeGrey" style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500 }}>
                                                            NA
                                                        </span>
                                                    ) : (
                                                        <span style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500, ...overrideBadgeStyle }}>
                                                            NA
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="drDmeRow">
                                                <Typography fontWeight={600} style={{ color: "#000" }}>DME</Typography>
                                                {reviewedDMEStatus ? (
                                                    <span className={reviewedDMEColorClass} style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500 }}>
                                                        {reviewedDMEStatus}
                                                    </span>
                                                ) : isGrey ? (
                                                    <span className="dmeBadgeGrey" style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500 }}>
                                                        NA
                                                    </span>
                                                ) : (
                                                    <span style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500, ...overrideBadgeStyle }}>
                                                        NA
                                                    </span>
                                                )}
                                            </div>
                                        </>
                                    );
                                }
                                
                                const drGrade = getDRGradeShort(inference.left);
                                const dmeGrade = dme.left;
                                // DME can't be reliably assessed on a Non Gradable image - suppress whatever
                                // DME the ophthalmologist recorded when displaying a Non Gradable review.
                                const dmeStatus = drGrade === "Non Gradable" ? null : getDMEStatus(dmeGrade);
                                const hasDR = drGrade !== null && drGrade !== undefined;
                                const hasDME = dmeStatus !== null && dmeStatus !== undefined;
                                
                                // Always show DR and DME rows (use NA when missing) for consistent mobile layout
                                const drBadgeStyle = hasDR ? getDRBadgeStyle(inference.left) : null;
                                
                                return (
                                    <>
                                        <div className="drDmeRow">
                                            <Typography fontWeight={600} style={{ color: "#000" }}>DR</Typography>
                                            <div className="drValueCell">
                                                {hasDR ? (
                                                    <span style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500, ...drBadgeStyle }}>
                                                        {drGrade}
                                                    </span>
                                                ) : (
                                                    <span className="drBadgeGrey" style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500 }}>
                                                        NA
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="drDmeRow">
                                            <Typography fontWeight={600} style={{ color: "#000" }}>DME</Typography>
                                            {hasDME ? (
                                                <span className={getDMEColorClass(dmeGrade)} style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500 }}>
                                                    {dmeStatus}
                                                </span>
                                            ) : (
                                                <span className="dmeBadgeGrey" style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "14px", fontWeight: 500 }}>
                                                    NA
                                                </span>
                                            )}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>
                
                {/* <Divider orientation="vertical" variant="middle" flexItem/>
                <div className="patientRecordsReportInfoSub">
                    <Typography color="#696969" fontWeight={500} display="flex" alignItems="center" gap="6px">
                        <CalendarMonthOutlinedIcon />
                        No. of Visits:
                    </Typography>
                    <Typography fontWeight={600}>
                        {report.visitsnumber}
                        Previous Visits
                    </Typography>
                </div> */}
                <Divider orientation="vertical" variant="middle" flexItem/>
                <div className="reportDiv">
                    {/* <div className="patientRecordsReportInfoSub">
                        <Typography color="#696969" fontWeight={500} display="flex" alignItems="center" gap="6px">
                            <img src="/assests/images/referredByIcon.svg" alt="" style={{width: "20px", height: "20px"}}/>
                            Referred by:
                        </Typography>
                        <Typography fontWeight={600} textTransform="capitalize">
                            {report?.doctor?.user?.name}
                        </Typography>
                    </div> */}
                    <div className="patientRecordsReportInfoSub">
                        <Typography color="#696969" fontWeight={500} display="flex" alignItems="center" gap="6px">
                        <CalendarMonthOutlinedIcon />
                            No. of {visitCount === 1 ? "visit" : "visits"}
                        </Typography>
                        <Typography fontWeight={600} textTransform="capitalize">
                            {visitCount}
                        </Typography>
                    </div>
                    {!isMobile && <Divider orientation="vertical" variant="middle" flexItem/>}
                    <div className="patientRecordsReportInfoSub" style={{paddingLeft: "8px"}}>
                        <Typography color="#696969" fontWeight={500} display="flex" alignItems="center" gap="6px">
                        <img src="/assests/images/referralIcon.svg" alt="" style={{width: "20px", height: "20px"}}/>
                            Referral:
                        </Typography>
                        <Typography fontWeight={600}>
                            {handleSetReferral(
                                inference.left,
                                inference.right,
                                report?.single_image_reason,
                                report?.ml_model?.operator_override || { left: { fundus: false, eye: false }, right: { fundus: false, eye: false } },
                                dme
                            )}
                        </Typography>
                    </div>
                </div>
                
                {isMobile && <div className="mobileViewReport">
                    <Button
                        disableElevation
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => handleViewReport(report.uuid)}
                        sx={{borderRadius: "3px", border: "1.5px solid var(--white, #FFF)", background: "#FFF", color: "#011F4B"}}
                    >
                        View Report
                    </Button>
                </div>}
            </div>
        </div>
    </div>
  ) 
}

export default PatientRecordsReport;